'use client'

/**
 * The platform's floating activity dock: a bar that stays across navigation and tells the person that
 * something long is going on (a search across 2,000 mailboxes, a remediation moving thousands of
 * copies), and a drawer that shows the live truth of each job: how many are done, how many are left,
 * how many failed and why, with the module's own buttons (pause, resume, cancel, retry failed, undo).
 *
 *  - Hidden when there is nothing to show; only polled for a role that can see some module's activity.
 *  - Finished work STAYS (owner, 3 Nov 2026: "The background jobs when completed should stay there, or we can
 *    clear somehow, so it will look natural and it will work natural"): the drawer lists it under "Finished"
 *    with its result and time until the person presses its Clear or Clear all finished; the server stops sending
 *    it 24 hours after it ended. The round button counts open work only, and shows a tick when all is done.
 *  - Rests with the Feedback launcher, never on it (lib/activities/placement.ts).
 *  - The drawer is the platform's non-modal Sheet from the inline end; the cards are Card / StatusBadge /
 *    PermissionButton; the numbers are the server's SQL aggregates, drawn, never recomputed.
 *  - A live region speaks CHANGES of state only (started, paused, finished, failed...), never a tick.
 *  - Reduced motion is respected; every sentence is a catalogue key; the layout is logical (RTL-safe).
 */

import * as React from 'react'
import { usePathname } from 'next/navigation'
import { Activity, AlertTriangle, CheckCircle2, ChevronDown, Loader2, Pause, X } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { formatLearnerNumber } from '@/components/user-portal/learner-i18n'
import { usePermissions } from '@/hooks/use-permissions'
import { useHydrated } from '@/hooks/use-hydrated'
import { useFabAutoCorner } from '@/hooks/use-fab-auto-corner'
import { useFabBarLift } from '@/hooks/use-fab-bar-lift'
import { useSidebarInset } from '@/hooks/use-fab-sidebar-inset'
import { useWorkspaceScopedEnabled } from '@/lib/api/use-workspace-scoped-enabled'
import { diffStates, seenStatesOf, type ActivityChange, type SeenStates } from '@/lib/activities/announce'
import { useActivityDockStore, visibleActivities } from '@/lib/activities/dock-store'
import { dockTransformFor } from '@/lib/activities/placement'
import { handled, isOpenState, isProgressing, type ActivityDto } from '@/lib/activities/types'
import { useActivities, type ActivitiesConnection } from '@/lib/activities/use-activities'
import { useTranslation } from '@/lib/i18n'
import type { I18nContextValue } from '@/lib/i18n'
import { isHiddenChromeRoute } from '@/lib/routing/hidden-route-prefixes'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores'
import { ActivityCard } from './activity-dock-card'
import { ACTIVITY_REFRESH_KEYS, ACTIVITY_REQUIREMENTS, needsAPerson, rendererFor } from './activity-dock-renderers'

type Translate = I18nContextValue['t']

// ─── What the bar says ──────────────────────────────────────────────────────────

function summarise(list: readonly ActivityDto[]) {
  const running = list.filter((a) => isProgressing(a.state))
  const paused = list.filter((a) => a.state === 'paused')
  // What a person must look at: it failed, in part or whole, and its module does not call that done.
  const attention = list.filter(needsAPerson)
  const lead = running[0] ?? paused[0] ?? attention[0] ?? list[0]
  const open = list.filter((a) => isOpenState(a.state))
  const total = open.reduce((s, a) => s + a.counts.total, 0)
  const over = open.reduce((s, a) => s + handled(a.counts), 0)
  return {
    running,
    paused,
    attention,
    lead,
    percent: total > 0 ? (over / total) * 100 : 0,
    hasOpen: open.length > 0,
    /** The round button's number: open work only (queued, running, paused), never what has ended. */
    openCount: open.length,
    /** Something that has ended is shown: the bar offers Clear finished. */
    hasOver: open.length < list.length,
  }
}

function headline(t: Translate, s: ReturnType<typeof summarise>, all: number): string {
  if (s.running.length > 0) return t('activities.dock.running', { count: s.running.length })
  if (s.paused.length > 0) return t('activities.dock.paused', { count: s.paused.length })
  if (s.attention.length > 0) return t('activities.dock.attention', { count: s.attention.length })
  return t('activities.dock.finished', { count: all })
}

function LeadIcon({ s }: { s: ReturnType<typeof summarise> }) {
  if (s.running.length > 0) return <Loader2 className="size-[18px] shrink-0 animate-spin text-muted-foreground motion-reduce:animate-none" aria-hidden />
  if (s.paused.length > 0) return <Pause className="size-[18px] shrink-0 text-muted-foreground" aria-hidden />
  if (s.attention.length > 0) return <AlertTriangle className="size-[18px] shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
  // All done (owner, 3 Nov 2026, Frame 5): a tick, and the round button carries no number.
  return <CheckCircle2 data-activity-done className="size-[18px] shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden />
}

// ─── What a screen reader is told ───────────────────────────────────────────────

/**
 * The sentence for the live region: the changes of state since the list was last read, and nothing
 * for a counter that moved. The first read is the baseline (work already running when the page
 * loaded is not "started"). Derived while rendering, so there is no effect to lag behind the list.
 */
export function useAnnouncement(list: readonly ActivityDto[] | null, t: Translate, n: (v: number) => string): string {
  const [memory, setMemory] = React.useState<{ list: readonly ActivityDto[] | null; seen: SeenStates; spoken: string }>({
    list: null,
    seen: new Map(),
    spoken: '',
  })
  if (list !== memory.list) {
    const changes: ActivityChange[] = list && memory.list ? diffStates(memory.seen, list) : []
    const spoken = changes.length
      ? changes
          .map((c) => {
            const title = rendererFor(c.activity.kind).titleText(c.activity, t, n)
            return t(`activities.announce.${c.kind}`, { title, failed: n(c.activity.counts.failed) })
          })
          .join(' ')
      : memory.spoken
    setMemory({ list, seen: list ? seenStatesOf(list) : memory.seen, spoken })
  }
  return memory.spoken
}

// ─── The view ───────────────────────────────────────────────────────────────────

export interface ActivityDockViewProps {
  activities: readonly ActivityDto[]
  unavailable: readonly string[]
  connection: ActivitiesConnection
  open: boolean
  onOpenChange: (open: boolean) => void
  expandedId: string | null | undefined
  onToggleExpanded: (id: string, current: string | null) => void
  minimized: boolean
  onMinimizedChange: (minimized: boolean) => void
  onDismiss: (a: ActivityDto) => void
  /** Take everything that has ended off the dock (failures included: a person chose to). */
  onClearFinished: () => void
  /** Where the bar rests (placement.ts): the same corner as the Feedback launcher, above it. */
  transform: string
  /** The feedback or chat panel is open in this corner: step out of its way. */
  stepAside: boolean
}

export function ActivityDockView(props: ActivityDockViewProps) {
  const { activities, unavailable, connection, open, onOpenChange, expandedId, onToggleExpanded, minimized, onMinimizedChange, onDismiss, onClearFinished, transform, stepAside } = props
  const { t, locale } = useTranslation()
  const n = (v: number) => formatLearnerNumber(v, locale)
  const pillRef = React.useRef<HTMLButtonElement>(null)
  const s = summarise(activities)
  const said = headline(t, s, activities.length)
  // Open work first; what has ended under its own "Finished" heading, in the order the server sent it.
  const going = activities.filter((a) => isOpenState(a.state))
  const ended = activities.filter((a) => !isOpenState(a.state))
  const cardOf = (a: ActivityDto) => (
    <ActivityCard key={a.id} activity={a} expanded={effective === a.id} onToggle={() => onToggleExpanded(a.id, effective)} onDismiss={onDismiss} />
  )
  // Until somebody chooses, the drawer shows the card that most needs looking at: what is going, else what failed, else the newest.
  const effective =
    expandedId === undefined
      ? (activities.find((a) => isOpenState(a.state)) ?? activities.find(needsAPerson) ?? activities[0])?.id ?? null
      : expandedId

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {/* The bar. Gone while the drawer is open (the drawer is the same thing, larger). */}
      {!open && activities.length > 0 && (
        <div
          data-slot="activity-dock"
          data-activity-dock={minimized ? 'minimized' : 'bar'}
          className={cn(
            // The box is as wide as what it holds, so the transform's `100%` puts its edge on the launcher's edge.
            'fixed top-0 z-40 will-change-transform',
            minimized ? 'w-12' : 'w-[min(22rem,calc(100vw-3rem))]',
            'transition-[transform,opacity] duration-500 ease-[cubic-bezier(.16,1,.3,1)] motion-reduce:transition-none',
            stepAside ? 'pointer-events-none opacity-0' : 'opacity-100',
          )}
          // Anchored at the window's top-left and moved by transform, exactly as the launcher is (FabStackHost):
          // its corners are physical, and the dock keeps to the same side of the window.
          style={{ left: 0, transform }}
        >
          {minimized ? (
            <div>
              <button
                type="button"
                ref={pillRef}
                aria-label={t('activities.dock.restoreAria', { summary: said })}
                onClick={() => onMinimizedChange(false)}
                className="relative flex size-12 items-center justify-center rounded-full border bg-popover text-popover-foreground shadow-lg hover:bg-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
              >
                <LeadIcon s={s} />
                {/* Open work only: with nothing going, no number (a failure shows its own icon). */}
                {s.openCount > 0 && (
                  <span
                    data-activity-count
                    className="absolute -end-1 -top-1 flex h-4 min-w-4 shrink-0 items-center justify-center whitespace-nowrap rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground tabular-nums"
                  >
                    {n(s.openCount)}
                  </span>
                )}
              </button>
            </div>
          ) : (
            <div className="rounded-2xl border bg-popover text-popover-foreground shadow-lg">
              <div className="flex items-stretch">
                <SheetTrigger asChild>
                  <button
                    type="button"
                    ref={pillRef}
                    aria-label={t('activities.dock.openAria', { summary: said })}
                    className={cn(
                      'flex min-w-0 flex-1 items-center gap-3 rounded-ss-2xl py-3 ps-3.5 pe-2 text-start hover:bg-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
                      !s.hasOver && 'rounded-es-2xl',
                    )}
                  >
                    <LeadIcon s={s} />
                    <span className="min-w-0 flex-1">
                      {/* The headline states the number itself: nothing beside it to wrap, and it truncates rather than push. */}
                      <span data-activity-headline className="block min-w-0 truncate text-sm font-medium">
                        {said}
                      </span>
                      {s.lead && (
                        <span className="mt-px block truncate text-xs text-muted-foreground">
                          {rendererFor(s.lead.kind).pillLine(s.lead, t, n)}
                        </span>
                      )}
                      {/* Decorative: the button's own name already says it (a button's children are presentational). */}
                      {s.hasOpen && <Progress value={s.percent} className="mt-2 h-1.5" aria-hidden />}
                    </span>
                  </button>
                </SheetTrigger>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="my-auto me-1 size-8 shrink-0 text-muted-foreground"
                  aria-label={t('activities.dock.minimizeAria')}
                  onClick={() => onMinimizedChange(true)}
                >
                  <ChevronDown className="size-4" aria-hidden />
                </Button>
              </div>
              {s.hasOver && (
                <div className="flex justify-end border-t px-2 py-1">
                  <Button
                    type="button"
                    size="xs"
                    variant="ghost"
                    className="shrink-0 whitespace-nowrap text-muted-foreground"
                    onClick={onClearFinished}
                    data-activity-clear-finished
                  >
                    <X className="size-3.5" aria-hidden />
                    {t('activities.dock.clearFinished')}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <SheetContent
        className="flex w-full flex-col gap-0 border-s p-0 sm:max-w-[550px]"
        onCloseAutoFocus={(e) => {
          // Back to the bar it was opened from, not to the top of the page.
          e.preventDefault()
          pillRef.current?.focus()
        }}
      >
        <SheetHeader className="shrink-0 border-b bg-muted/20 p-4">
          <div className="flex items-center gap-3 pe-8">
            <Activity className="size-4 text-primary" aria-hidden />
            <div>
              <SheetTitle className="text-lg font-semibold tracking-tight">{t('activities.drawer.title')}</SheetTitle>
              <SheetDescription className="flex flex-wrap items-center gap-x-2 text-xs">
                <ConnectionTag connection={connection} />
                <span aria-hidden>·</span>
                <span>{t('activities.drawer.subtitle')}</span>
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4" data-activity-list>
          {unavailable.length > 0 && (
            <Alert variant="warning" data-activity-unavailable>
              <AlertTriangle aria-hidden />
              <AlertDescription>{t('activities.unavailable')}</AlertDescription>
            </Alert>
          )}
          {activities.length === 0 && unavailable.length === 0 && (
            <p className="py-6 text-center text-sm text-muted-foreground">{t('activities.drawer.empty')}</p>
          )}
          {going.map(cardOf)}
          {ended.length > 0 && (
            <div data-activity-finished-heading className="flex items-center justify-between gap-2 pt-1">
              <h3 className="min-w-0 truncate text-sm font-semibold">{t('activities.drawer.finished')}</h3>
              <Button
                type="button"
                size="xs"
                variant="ghost"
                className="shrink-0 whitespace-nowrap text-muted-foreground"
                onClick={onClearFinished}
                data-activity-clear-finished
              >
                <X className="size-3.5" aria-hidden />
                {t('activities.dock.clearFinished')}
              </Button>
            </div>
          )}
          {ended.map(cardOf)}
        </div>

        <div className="shrink-0 border-t px-4 py-3 text-xs text-muted-foreground">{t('activities.drawer.footer')}</div>
      </SheetContent>
    </Sheet>
  )
}

function ConnectionTag({ connection }: { connection: ActivitiesConnection }) {
  const { t } = useTranslation()
  if (connection === 'live') {
    return (
      <span className="inline-flex items-center gap-1.5" data-activity-connection="live">
        <i className="size-1.5 animate-pulse rounded-full bg-emerald-500 motion-reduce:animate-none" aria-hidden />
        {t('activities.drawer.live')}
      </span>
    )
  }
  if (connection === 'offline') {
    return (
      <span className="text-amber-700 dark:text-amber-400" data-activity-connection="offline">
        {t('activities.drawer.offline')}
      </span>
    )
  }
  return <span data-activity-connection="polling">{t('activities.drawer.polling')}</span>
}

// ─── The dock in the shell ──────────────────────────────────────────────────────

/**
 * Mounted once in the root layout beside the Feedback launcher. It reads the viewer's activity,
 * keeps the live region, and draws the bar and the drawer when there is something to show.
 */
export function ActivityDock() {
  const pathname = usePathname()
  const { t, locale } = useTranslation()
  const n = (v: number) => formatLearnerNumber(v, locale)
  const hydrated = useHydrated()
  const isAuthenticated = useAuthStore((st) => st.isAuthenticated)
  const scoped = useWorkspaceScopedEnabled()
  const perms = usePermissions()

  // Only for a role that can see some module's activity (unknown while the grants load: ask, the API refuses).
  const allowed = perms.canAny(ACTIVITY_REQUIREMENTS) !== false
  const enabled = hydrated && isAuthenticated && scoped && allowed && !isHiddenChromeRoute(pathname ?? '')

  const { activities, unavailable, loaded, connection } = useActivities({ enabled, refreshKeys: ACTIVITY_REFRESH_KEYS })
  const dock = useActivityDockStore()
  const visible = React.useMemo(() => visibleActivities(activities ?? [], dock.dismissed), [activities, dock.dismissed])
  const announcement = useAnnouncement(enabled && loaded ? visible : null, t, n)

  // Where the Feedback launcher rests right now, and so where the dock does.
  const { effectiveCorner, direction, liftAboveActions } = useFabAutoCorner()
  const sidebarInset = useSidebarInset(pathname)
  const barLift = useFabBarLift(effectiveCorner, direction, sidebarInset)
  const transform = dockTransformFor(effectiveCorner, sidebarInset, direction, liftAboveActions, barLift)

  // The feedback / chat panel opens beside the launcher: the launcher hides, and so does the dock.
  const [stepAside, setStepAside] = React.useState(false)
  React.useEffect(() => {
    const on = () => setStepAside(true)
    const off = () => setStepAside(false)
    window.addEventListener('fab-widget:open', on)
    window.addEventListener('fab-widget:close', off)
    return () => {
      window.removeEventListener('fab-widget:open', on)
      window.removeEventListener('fab-widget:close', off)
    }
  }, [])

  if (!enabled) return null
  return (
    <>
      {/* Present before anything changes, so the first change is heard. State changes only. */}
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only" data-activity-announcer>
        {announcement}
      </div>
      {(visible.length > 0 || dock.open) && (
        <ActivityDockView
          activities={visible}
          unavailable={unavailable}
          connection={connection}
          open={dock.open}
          onOpenChange={(o) => (o ? dock.openDock() : dock.closeDock())}
          expandedId={dock.expandedId}
          onToggleExpanded={dock.toggleExpanded}
          minimized={dock.minimized}
          onMinimizedChange={dock.setMinimized}
          onDismiss={dock.dismiss}
          onClearFinished={() => dock.dismissAll(visible)}
          transform={transform}
          stepAside={stepAside}
        />
      )}
    </>
  )
}

